import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import AddNewsModal from "./AddNewsModal";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { keyframes } from "styled-components";
import openNotification from "components/utils/CustomNotification";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const LatestNews: React.FC = () => {
  useCheckSessionHeartBeat();
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const [activeIndex, setActiveIndex] = useState(0);
  const [dotsArray, setDotsArray] = useState<number[]>([]);
  const [newsItems, setNewsItems] = useState<any[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  // const privilege = localStorage.getItem("privilege") || "";
  // const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [editData] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      if (newsItems.length > 1) {
        setActiveIndex((prev) => (prev + 1) % newsItems.length);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [newsItems.length]);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const toDateUTC = now.toISOString();
      const today2 = new Date();
      const fromDateUTC = today2.toISOString();

      const newsResponse = await axios.get(`${beAddress}/api/carousal`, {
        headers: {
          "user-uid": userUid,
        }, withCredentials: true,
        params: {
          from_datetime: fromDateUTC,
          to_datetime: toDateUTC,
        },

      });

      if (newsResponse.data?.status === "Success" && newsResponse.data.data.length > 0) {
        const newsData = newsResponse.data.data;
        const imageUids = newsData.map((item: any) => item.image_uid);

        const imagesResponse = await axios.get(`${beAddress}/api/get-image-by-uid`, {
          headers: {
            "user-uid": userUid,
          }, withCredentials: true,
          params: {
            image_uid_list: imageUids,
            is_icon_img: false,
          },
          paramsSerializer: params =>
            Object.keys(params)
              .map(key =>
                Array.isArray(params[key])
                  ? params[key].map((val: string) => `${key}=${encodeURIComponent(val)}`).join("&")
                  : `${key}=${encodeURIComponent(params[key])}`
              )
              .join("&"),
        });

        if (imagesResponse.data?.status === "Success") {
          const images = imagesResponse.data.data || [];

          const newsWithImages = newsData.map((newsItem: any) => ({
            ...newsItem,
            image: images.find((img: any) => img.image_uid === newsItem.image_uid)?.image || null
          }));

          const now = new Date();
          const filteredNews = filterCarousalsInTimeRange(newsWithImages, now, now);

          const validNews = filteredNews.filter(
            (item: any) => item.image && item.image.trim().length > 30
          );

          setNewsItems(validNews);
          setDotsArray(Array.from({ length: validNews.length }, (_, i) => i));


          setDotsArray(Array.from({ length: newsWithImages.length }, (_, i) => i));
        } else {
          openNotification({
            type: "error",
            message: "Failed to fetch Images"
          });
          setNewsItems(newsData);
        }
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "Failed to "
      });
    } finally {
      setLoading(false);
    }
  }, [beAddress, sessionUid, userUid]);


  const filterCarousalsInTimeRange = (
    carousals: any[],
    currentStartTime: Date,
    currentEndTime: Date,
  ) => {
    const currentFromMinutes = currentStartTime.getHours() * 60 + currentStartTime.getMinutes();
    const currentToMinutes = currentEndTime.getHours() * 60 + currentEndTime.getMinutes();

    return carousals.filter(item => {
      const itemFrom = new Date(item.from_datetime);
      const itemTo = new Date(item.to_datetime);

      const itemFromMinutes = itemFrom.getHours() * 60 + itemFrom.getMinutes();
      const itemToMinutes = itemTo.getHours() * 60 + itemTo.getMinutes();

      return itemFromMinutes <= currentToMinutes && itemToMinutes >= currentFromMinutes;
    });
  };

  const handleNext = () => {
    if (newsItems.length > 1) {
      setActiveIndex((prev) => (prev + 1) % newsItems.length);
    }
  };

  const handlePrev = () => {
    if (newsItems.length > 1) {
      setActiveIndex((prev) => (prev - 1 + newsItems.length) % newsItems.length);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  return (
    <LatestNewsMainContainer>
      {loading ? (
        <LoaderCard>
          <LoaderIcon>📰</LoaderIcon>
          <LoaderText>Loading latest news...</LoaderText>
        </LoaderCard>
      ) : newsItems.length > 0 ? (
        <NewsRow>
          <ImageBlock>
            <img
              src={`data:image/jpeg;base64,${newsItems[activeIndex]?.image}`}
              srcSet={`data:image/jpeg;base64,${newsItems[activeIndex]?.image} 1x, data:image/jpeg;base64,${newsItems[activeIndex]?.image} 2x`}
              alt={newsItems[activeIndex]?.title || "News"}
            />

            {/* Only show controls if more than 1 news item */}
            {newsItems.length > 1 && (
              <CarouselControls>
                <ChevronLeft className="arrow" onClick={handlePrev} />
                <span>{activeIndex + 1} / {newsItems.length}</span>
                <ChevronRight className="arrow" onClick={handleNext} />
              </CarouselControls>
            )}
          </ImageBlock>
        </NewsRow>
      ) : (
        <>
          {activeIndex % 2 === 0 ? (
            <EmptyCard>
              <EmptyIcon>📢</EmptyIcon>
              <EmptyTitle>No News to Display</EmptyTitle>
              <EmptySubtitle>Stay tuned for exciting updates!</EmptySubtitle>
            </EmptyCard>
          ) : (
            <MotivationCard>
              <MotivationIcon>🏃‍♂️🎯</MotivationIcon>
              <MotivationTitle>Complete Your Fitness Journey</MotivationTitle>
              <MotivationSubtitle>Make teams and achieve goals with Cofit!</MotivationSubtitle>
            </MotivationCard>
          )}
        </>
      )}

      {/* Show pagination dots only if multiple news items */}
      {newsItems.length > 1 && (
        <Pagination>
          <MovingDots>
            {newsItems.map((_, index) => (
              <Dot key={index} active={index === activeIndex} />
            ))}
          </MovingDots>
        </Pagination>
      )}

      {showAddModal && (
        <AddNewsModal onClose={() => setShowAddModal(false)} onSuccess={fetchNews} />
      )}

      {showEditModal && editData && (
        <AddNewsModal
          onClose={() => setShowEditModal(false)}
          onSuccess={fetchNews}
          initialData={editData}
          isEdit={true}
        />
      )}
    </LatestNewsMainContainer>
  );

};

export default LatestNews;

const LatestNewsMainContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--card-background, #fff);
  padding: 0; /* Remove padding to let image fill full */
  border-radius: 15px;
  box-shadow: 2px 4px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  width: 100%;
  height: 100%; /* Fill the flex parent */
  min-height: 240px; /* Force it to be taller */
  overflow: hidden;
`;

// 🔹 Right Image
// const NewsImage = styled.div`
//   width: 400px;
//   height: 100%; 
//   overflow: hidden;
//   border-radius: 8px;

//   img {
//     width: 100%;
//     height: 100%;
//     object-fit: cover;
//     display: block;
//   }
// `;
// 🔹 Pagination Marquee
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
`;

const MovingDots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.span<{ active?: boolean }>`
  width: ${props => (props.active ? '12px' : '8px')};
  height: 8px;
  border-radius: 4px;
  background: ${props => (props.active ? '#007bff' : '#ccc')};
  transition: all 0.3s ease;
`;
const EmptyCard = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #e0f7fa, #f1f8e9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 180px;
  border-radius: 10px;
  padding: 20px;
`;

const MotivationCard = styled(EmptyCard)`
  background: linear-gradient(135deg, #e8f5e9, #e3f2fd);
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 10px;
`;

const MotivationIcon = styled.div`
  font-size: 48px;
  margin-bottom: 10px;
`;

const EmptyTitle = styled.h2`
  font-size: 1.3rem;
  color: #004d40;
  margin-bottom: 6px;
`;

const MotivationTitle = styled.h2`
  font-size: 1.3rem;
  color: #1565c0;
  margin-bottom: 6px;
`;

const EmptySubtitle = styled.p`
  font-size: 1rem;
  color: #333;
`;

const MotivationSubtitle = styled.p`
  font-size: 1rem;
  color: #555;
`;
const NewsRow = styled.div`
  width: 100%;
  height: 100%;
`;

const ImageBlock = styled.div`
  width: 100%;
  height: 100%;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* This makes the image stretch to fill height without distortion */
    display: block;
  }
`;
const pulse = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;
const LoaderCard = styled.div`
  flex: 1;
  height: 200px;
  background: linear-gradient(135deg, #e3f2fd, #f1f8e9);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 1.5s infinite ease-in-out;
  animation: pulse 1.5s infinite ease-in-out;
`;
const LoaderIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 8px;
`;
const LoaderText = styled.div`
  font-size: 1rem;
  color: #555;
`;
const CarouselControls = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  gap: 10px;
  .arrow {
    cursor: pointer;
    font-size: 1.2rem;
    transition: transform 0.2s ease;
  }
  .arrow:hover {
    transform: scale(1.2);
  }
`;